"use client";

import React, { createContext, useContext, useState } from "react";

export type Message = {
    type: "question" | "answer" | "loading" | "widget" | "title";
    content: string;
    id: string;
    parent_id: string | null;
    created_id?: string;

    created_at: number;
};

export interface MessageFromAPI {
    content: {
        type: string;
        data: string;
    };
    data: string;
    type: string;
    conversation_id: string;
    created_at: number;
    id: string;
    parent_message_id: string;
    updated_at: number;
    user_id: string;
}

type ExploreContextType = {
    conversationId: string;
    setConversationId: (id: string) => void;

    hasLoadedInitialData: boolean;
    setHasLoadedInitialData: (loaded: boolean) => void;

    hasPreviousConversation: boolean;
    setHasPreviousConversation: (prev: boolean) => void;

    suggestions: string[];
    setSuggestions: (sugs: string[]) => void;

    messages: Message[];
    setMessages: (msgs: Message[]) => void;

    inputValue: string;
    setInputValue: (val: string) => void;

    isLoading: boolean;
    setIsLoading: (loading: boolean) => void;

    isLoadingOldChats: boolean;
    setIsLoadingOldChats: (loading: boolean) => void;

    isSubmitting: boolean;
    setIsSubmitting: (submitting: boolean) => void;

    isUserChecked: boolean;
    setIsUserChecked: (checked: boolean) => void;

    conversationStarted: boolean;
    setConversationStarted: (started: boolean) => void;

    disableLoadMoreChat: boolean;
    setDisableLoadMoreChat: (val: boolean) => void;

    loadOldChatsButton: boolean;
    setLoadOldChatsButton: (val: boolean) => void;

    showScrollTop: boolean;
    setShowScrollTop: (val: boolean) => void;
    atTop: boolean;
    setAtTop: (val: boolean) => void;

    scrollAmount: any;
    setScrollAmount: any;
    activeConversation: Message[];
    setActiveConversation: any;
};

const ExploreContext = createContext<ExploreContextType | undefined>(undefined);

export const ExploreProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const [conversationId, setConversationId] = useState("");
    const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
    const [hasPreviousConversation, setHasPreviousConversation] =
        useState(false);
    const [suggestions, setSuggestions] = useState<string[]>([]);

    const [messages, setMessages] = useState<Message[]>([]);

    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingOldChats, setIsLoadingOldChats] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);

    const [scrollAmount, setScrollAmount] = useState<any>();

    const [activeConversation, setActiveConversation] = useState<Message[]>([]);

    const [atTop, setAtTop] = useState(true);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [isUserChecked, setIsUserChecked] = useState(false);

    const [disableLoadMoreChat, setDisableLoadMoreChat] = useState(false);
    const [loadOldChatsButton, setLoadOldChatsButton] = useState(false);

    return (
        <ExploreContext.Provider
            value={{
                conversationId,
                setConversationId,
                hasLoadedInitialData,
                setHasLoadedInitialData,
                hasPreviousConversation,
                setHasPreviousConversation,
                suggestions,
                setSuggestions,

                messages,
                setMessages,
                activeConversation,
                setActiveConversation,

                inputValue,
                setInputValue,
                isLoading,
                setIsLoading,
                isLoadingOldChats,
                setIsLoadingOldChats,
                showScrollTop,
                setShowScrollTop,
                setAtTop,
                atTop,
                isSubmitting,
                setIsSubmitting,

                scrollAmount,
                setScrollAmount,
                isUserChecked,
                setIsUserChecked,

                disableLoadMoreChat,
                setDisableLoadMoreChat,
                loadOldChatsButton,
                setLoadOldChatsButton,
            }}
        >
            {children}
        </ExploreContext.Provider>
    );
};

export const useExploreContext = (): ExploreContextType => {
    const context = useContext(ExploreContext);
    if (!context) {
        throw new Error(
            "useExploreContext must be used within ExploreProvider",
        );
    }
    return context;
};
